/**
 * useSetVersionTag Hook
 *
 * React Query mutation for setting a version tag on a deployment.
 * Note: Version tags are immutable once set.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { deploymentService } from '../../../api/deployment.service';
import type { DeploymentSanitized } from '../../../types/deployment.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseSetVersionTagOptions {
  tenantId: string;
  applicationId: string;
  onSuccess?: (data: DeploymentSanitized) => void;
  onError?: (error: Error) => void;
}

interface SetVersionTagVariables {
  deploymentId: string;
  versionTag: string;
}

/**
 * Hook for setting a version tag on a deployment
 */
export const useSetVersionTag = (
  options: UseSetVersionTagOptions
): UseMutationResult<DeploymentSanitized, Error, SetVersionTagVariables> => {
  const { tenantId, applicationId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<DeploymentSanitized, Error, SetVersionTagVariables>({
    mutationFn: async ({ deploymentId, versionTag }: SetVersionTagVariables) => {
      console.log('[useSetVersionTag] Setting version tag:', versionTag, 'on deployment:', deploymentId);
      return await deploymentService.updateDeployment(tenantId, deploymentId, { versionTag });
    },
    onSuccess: (data) => {
      console.log('[useSetVersionTag] Version tag set successfully:', data.versionTag);
      // Invalidate deployments list query
      queryClient.invalidateQueries({ queryKey: ['deployments', tenantId, applicationId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      console.error('[useSetVersionTag] Failed to set version tag:', getErrorMessage(error));
      onError?.(error);
    },
  });
};
